import { useFieldArray, useFormContext, get } from 'react-hook-form';
import { Plus, Trash } from 'lucide-react';
import { TextInput } from '@/components/form/TextInput.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import TfgVariantTypingFields from '@/features/dashboard/tfg/components/TfgVariantTypingFields.tsx';
import { TransportInputForm } from '@/features/dashboard/tourTransport/schemas/transportSchema.ts';
import { ParticipantLimitMode } from '@/features/dashboard/tourTransport/types/ParticipantLimitMode.ts';
import { participantLimitModeToggleOptions } from '@/features/dashboard/tourTransport/types/ParticipantLimitMode.utils.ts';
import { optionalNumberValueAs } from '@/utils/formUtils.ts';

const TransportVariantForm = ({
  index,
  remove,
}: {
  index: number;
  remove: (index: number) => void;
}) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<TransportInputForm>();

  const currency = watch(`variants.${index}.currency`);
  const participantLimitMode = watch(`variants.${index}.participantLimitMode`);
  const isPriceByAge = watch(`variants.${index}.isPriceByAge`);

  const ageRangePricesFieldArray = useFieldArray({
    control,
    name: `variants.${index}.ageRangePrices`,
  });

  const handleParticipantLimitModeChange = (value: string) => {
    if (value === ParticipantLimitMode.UNLIMITED) {
      setValue(`variants.${index}.participantLimit`, undefined);
    }
  };

  const handleIsPriceByAgeToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setValue(`variants.${index}.flatPrice`, undefined);
      if (ageRangePricesFieldArray.fields.length === 0) {
        ageRangePricesFieldArray.append({
          minAge: undefined,
          maxAge: undefined,
          amount: undefined,
        });
      }
    } else {
      ageRangePricesFieldArray.replace([]);
    }
  };

  const variantErrors = errors.variants?.[index];

  return (
    <div className="overflow-hidden rounded-md border border-neutral-200">
      <div className="h-1 w-full bg-accent-600" />
      <div className="bg-accent-100 p-3">
        <div className="flex flex-row items-end gap-2">
          <div className="flex-1">
            <TwoInputsRow ratio="3/1">
              <TextInput
                label="Nazwa wariantu"
                register={register(`variants.${index}.name`)}
                placeholder="np. Autokar"
                required
                error={variantErrors?.name?.message}
              />
              <SingleSelectInput
                fieldName={`variants.${index}.currency`}
                label="Waluta"
                placeholder="Wybierz"
                required
                control={control}
                options={currencySelectOptions}
                error={variantErrors?.currency?.message}
              />
            </TwoInputsRow>
          </div>
          <DangerIconButton onClick={() => remove(index)}>
            <Trash />
          </DangerIconButton>
        </div>
      </div>

      <div className="space-y-4 bg-basic-white p-3">
        <TfgVariantTypingFields index={index} />

        <TextInput
          label="Opis"
          register={register(`variants.${index}.description`)}
          placeholder="Dodatkowe informacje o transporcie (opcjonalnie)"
          error={variantErrors?.description?.message}
        />

        <TwoInputsRow ratio="2/1">
          <ToggleInput
            fieldPath={`variants.${index}.participantLimitMode`}
            label="Limit rezerwacji"
            options={participantLimitModeToggleOptions}
            control={control}
            onChange={handleParticipantLimitModeChange}
            error={variantErrors?.participantLimitMode?.message}
          />
          <ValueInput
            label="Liczba miejsc"
            register={register(`variants.${index}.participantLimit`, {
              setValueAs: optionalNumberValueAs,
            })}
            placeholder="0"
            sufix="miejsc"
            disabled={participantLimitMode !== ParticipantLimitMode.LIMITED}
            error={variantErrors?.participantLimit?.message}
          />
        </TwoInputsRow>

        <CheckboxInput
          label="Cena zależna od wieku"
          register={register(`variants.${index}.isPriceByAge`)}
          onChange={handleIsPriceByAgeToggle}
          error={variantErrors?.isPriceByAge?.message}
        />

        {!isPriceByAge && (
          <TwoInputsRow ratio="1/2">
            <ValueInput
              label="Cena"
              register={register(`variants.${index}.flatPrice`, {
                setValueAs: optionalNumberValueAs,
              })}
              placeholder="0"
              type="number"
              step={0.01}
              sufix={currency}
              required
              error={variantErrors?.flatPrice?.message}
            />
          </TwoInputsRow>
        )}

        {isPriceByAge && (
          <div className="space-y-1">
            <div className="grid grid-cols-[1fr_1fr_1fr_34px] gap-2">
              <InputLabel label="Wiek minimalny" required />
              <InputLabel label="Wiek maksymalny" required />
              <InputLabel label="Cena" required />
              <div />
            </div>
            {ageRangePricesFieldArray.fields.map((field, priceIndex) => (
              <div
                key={field.id}
                className="grid grid-cols-[1fr_1fr_1fr_34px] gap-2"
              >
                <ValueInput
                  register={register(
                    `variants.${index}.ageRangePrices.${priceIndex}.minAge`,
                    { setValueAs: optionalNumberValueAs },
                  )}
                  placeholder="0"
                  sufix="lat"
                  error={get(
                    errors,
                    `variants.${index}.ageRangePrices.${priceIndex}.minAge.message`,
                  )}
                />
                <ValueInput
                  register={register(
                    `variants.${index}.ageRangePrices.${priceIndex}.maxAge`,
                    { setValueAs: optionalNumberValueAs },
                  )}
                  placeholder="0"
                  sufix="lat"
                  error={get(
                    errors,
                    `variants.${index}.ageRangePrices.${priceIndex}.maxAge.message`,
                  )}
                />
                <ValueInput
                  register={register(
                    `variants.${index}.ageRangePrices.${priceIndex}.amount`,
                    { valueAsNumber: true },
                  )}
                  placeholder="0"
                  sufix={currency}
                  error={get(
                    errors,
                    `variants.${index}.ageRangePrices.${priceIndex}.amount.message`,
                  )}
                />
                <DangerIconButton
                  onClick={() => ageRangePricesFieldArray.remove(priceIndex)}
                >
                  <Trash />
                </DangerIconButton>
              </div>
            ))}

            <InputError
              error={
                get(errors, `variants.${index}.ageRangePrices.message`) ??
                get(errors, `variants.${index}.ageRangePrices.root.message`)
              }
            />

            <div className="pt-1">
              <ActionButton
                tone="outline"
                size="xs"
                onClick={() =>
                  ageRangePricesFieldArray.append({
                    minAge: undefined,
                    maxAge: undefined,
                    amount: undefined,
                  })
                }
              >
                <Plus />
                Dodaj grupę wiekową
              </ActionButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransportVariantForm;
